'use client'

import { useState, useEffect, useContext } from 'react'
import { RootContext } from '@/modules/contextService'
import SubscribeButton from '@/components/inputs/SubscribeButton'
import LikeButton from '@/components/inputs/LikeButton'
import Chip from '@mui/material/Chip'
import BarChartIcon from '@mui/icons-material/BarChart'
import { IPublisher } from '@/interfaces/IPublisher'
import { IPostInterface } from '@/interfaces/IPostInterface'
import styles from './StatsSection.module.scss'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ShareIcon from '@mui/icons-material/Share'
import numeral from 'numeral'
import { PATHWAY } from '@/modules/httpService'

interface StatsSectionProps {
  post: IPostInterface
  viewCount: number
  shareCount: number
  publisher: IPublisher
}

function StatsSection ({ post, viewCount, shareCount, publisher }: StatsSectionProps) {
  const { rootData } = useContext(RootContext)
  const { user } = rootData
  const [likeCount, setLikeCount] = useState<number>(0)
  const [preserveStatus, setPreserveStatus] = useState<string>('none')
  const [likeStatus, setLikeStatus] = useState<string>('none')
  const [loading, setLoading] = useState<boolean>(true)
  const [sorryModalOpen, setSorryModalOpen] = useState<boolean>(false)

  async function updateServer (dir: string) {
    try {
      await PATHWAY.put(`/posts/${post.id}/likes`, { dir })
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err.message)
        if (preserveStatus === 'up') {
          setLikeStatus('up')
          setLikeCount(likeCount - 1)
        }
        if (preserveStatus === 'down') setLikeStatus('down')
        if (preserveStatus === 'none') setLikeStatus('none')
      }
    }
  }

  async function loadLikes () {
    try {
      const res = await PATHWAY.get(`/self/likes?postId=${post.id}`)
      const record = res.data.data.likes[0]
      if (record) {
        setLikeStatus(record.dir)
      }
      setLoading(false)
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err.message)
      }
    }
  }

  async function handleLike () {
    // if (!user) {
    //   setSorryModalOpen(true)
    //   return
    // }
    // if (loading) return

    setPreserveStatus(likeStatus)
    if (likeStatus === 'none') {
      setLikeCount(likeCount + 1)
      setLikeStatus('up')
      await updateServer('up')
    }
  }

   /**
   * This function first checks if likeCount is within the range where you want to
   * show more precision (in this example, 950,000 to 999,999). If it is, it formats
   * the number with a comma as a thousand separator and one decimal place,
   * appending 'k' manually. For numbers outside this range, it falls back to
   * the default numeral.js formatting. This approach gives you more control
   * over the formatting of specific number ranges.
   */
  function formatLikes(likeCount: number): string {
    // Check if the number is close to a million (e.g., 950,000 to 999,999)
    if (likeCount >= 950000 && likeCount < 1000000) {
      // Use a more precise format for this range
      return numeral(likeCount).format('0,0.0').replace('.0', '') + 'k'
    } else {
      // Use the default formatting for other numbers
      return numeral(likeCount).format('0.0a').replace('.0', '')
    }
  }

  useEffect(() => {
    console.log('post', post)
    setLikeCount(post.zoom.likes)
    setLikeStatus('none')
    setPreserveStatus('none')
    loadLikes()
  }, [post])

  return (
    <div className={styles.upperWrapper}>
      <div className={styles.subscribeSection}>
        <SubscribeButton
          username={publisher.username}
          userId={publisher.id}
        />
      </div>
      <div className={styles.chipSection}>
        <Chip
          avatar={<BarChartIcon />}
          label={`${viewCount}`}
          className={styles.viewCountChip}
        />
      </div>
      {(shareCount || 0) > 0 &&
        <div className={styles.chipSection}>
          <Chip
            avatar={<ShareIcon />}
            label={`${shareCount}`}
            className={styles.viewCountChip}
          />
        </div>
      }
      <div className={styles.likeCountChip}>
        <Chip
          avatar={<ThumbUpIcon style={{ height: 16, width: 16, marginLeft: 12 }} />}
          label={formatLikes(likeCount)}
          className={styles.viewCountChip}
          style={{ marginRight: 10 }}
        />
      </div>
      <div className={styles.likeSection}>
        <LikeButton
          postId={post.id}
          likes={post.zoom.likes}
          likeStatus={likeStatus}
          sorryModalOpen={sorryModalOpen}
          user={user}
          loading={loading}
          clickLike={() => handleLike()}
          setSorryModalBool={bool => setSorryModalOpen(bool)}
        />
      </div>
    </div>
  )
}

export default StatsSection
